// @ts-ignore
import linkHeaderParser from 'linkheader-parser/dist/linkheader-parser-node';

import type { ApiResponse } from '../../../index';
import globals from '../../globals';

import type HttpMethod from '../types/HttpMethod';
import type Range from '../types/Range';
import type RequestBody from '../types/RequestBody';
import type ResponseBody from '../types/ResponseBody';
import type RequestHeaders from '../types/RequestHeaders';
import type RequestOptions from '../types/RequestOptions';
import type Response from '../types/Response';
import type ResponseHeaders from '../types/ResponseHeaders';
import type Interceptor from '../types/Interceptor';
import type InterceptorRequestData from '../types/InterceptorRequestData';
import type ParamCollection from '../types/ParamCollection';
import type Serializable from '../types/Serializable';
import type RequestInteface from '../types/RequestInterface';

import ajax from '../services/ajax';

import Pagination from './Pagination';

type PromiseResolver = (value: any) => any;

type QueuedRequest<T extends ResponseBody | void = void> = {
  request: Request<T>;
  resolve: PromiseResolver;
  reject: PromiseResolver;
};

function deepClone<T>(obj: T): T {
  return JSON.parse(JSON.stringify(obj));
}

function serialized(jsObject: ParamCollection): string {
  return Object.keys(jsObject)
    .map((param) => `${param}=${encodeURIComponent(jsObject[param])}`)
    .join('&');
}

function isStatusOk(statusCode: number): boolean {
  return statusCode >= 200 && statusCode <= 299;
}

function isPartialResponse(statusCode: number): boolean {
  return statusCode === 206;
}

/**
 * @private
 * */
export function parseContentRangeResponseHeader(headers: ResponseHeaders = {}): Range {
  let start = 0;
  let end = 0;
  let totalElements = 0;

  const header = headers['content-range'] || '';

  if (!header) {
    return {
      start,
      end,
      totalElements,
    };
  }

  // header is a string with this format: "items 0-5/8"
  const headerData = header.split(' ')[1].split('/');
  const paginationData = headerData[0].split('-');

  start = +paginationData[0];
  end = +paginationData[1];
  totalElements = +headerData[1];

  return {
    start,
    end,
    totalElements,
  };
}

function getPaginationFromHeaders(headers: ResponseHeaders, request: Request): Pagination {
  const headerRange = parseContentRangeResponseHeader(headers);
  const elementsByPage = headerRange.end + 1 - headerRange.start;

  return new Pagination(headerRange.start, elementsByPage, headerRange.totalElements, request);
}

function getParsedResponseHeaders(stringifiedResponseHeaders: string): ResponseHeaders {
  const headerSeparator = ': ';

  return stringifiedResponseHeaders
    .split('\n')
    .reduce((headers: ResponseHeaders, stringHeader: string) => {
      const headerSegment = stringHeader.split(headerSeparator);

      if (headerSegment.length < 2) {
        return headers;
      }

      return {
        ...headers,
        [headerSegment[0].toLowerCase()]: headerSegment[1],
      };
    }, {});
}

/**
 * @desc A representation of an HTTP Request.
 */
class Request<EntityType extends ResponseBody | void = void>
  implements RequestInteface<EntityType>
{
  url: string;

  method: HttpMethod;

  body: RequestBody;

  queryParams: ParamCollection;

  routeParams: ParamCollection;

  uploadOptions: Record<string, Serializable | Function>;

  requestInterceptors: Interceptor<InterceptorRequestData>[];

  responseInterceptors: Interceptor<Response>[];

  currentXhr: XMLHttpRequest | null;

  headers: RequestHeaders;

  /**
   * @param {String} url The URL to target.
   * @param {String} [method='GET'] The method to use (GET, POST, etc.).
   * @param {String} [language='en'] Language to accept.
   * @example const request = new OC_SDK.Request('https://my_url.com');
   * @example const request = new OC_SDK.Request('https://my_url.com', 'POST');
   * @example const request = new OC_SDK.Request('https://my_url.com/:myParam1', 'GET', 'fr');
   */
  constructor(url: string, method: HttpMethod = 'GET', language: string = 'en') {
    /**
     * @private
     * @desc The URL to target.
     * @type {String}
     */
    this.url = url;

    /**
     * @private
     * @desc The method to use (GET, POST, etc.).
     * @type {String}
     */
    this.method = this.setMethod(method);

    /**
     * @private
     * @desc Body request: `{ paramName: paramValue }`
     * @type {Object}
     */
    this.body = {};

    /**
     * @private
     * @desc Params to append to request URL: `{ paramName: paramValue }`
     * @type {Object}
     */
    this.queryParams = {};

    /**
     * @private
     * @desc Params to set into URL: `{ paramName: paramValue }`
     * @type {Object}
     */
    this.routeParams = {};

    /**
     * @private
     * @desc Option to set xhr.upload object: `{ optionName: paramValue }`
     * @type {Object}
     */
    this.uploadOptions = {};

    /**
     * @private
     * @desc Functions to run before executing the request.
     * @type {Array<Function>}
     */
    this.requestInterceptors = [];

    /**
     * @private
     * @desc Functions to run after executing the request.
     * @type {Array<Function>}
     */
    this.responseInterceptors = [];

    /**
     * @private
     * @desc Xhr object handling current Ajax request.
     * @type {XMLHttpRequest}
     */
    this.currentXhr = null;

    /**
     * @private
     * @desc An object containing headers properties.
     * @type {Object}
     */
    this.headers = {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      'Accept-Language': language,
      'X-Requested-With': 'XMLHttpRequest',
    };
  }

  /**
   * @desc Set the request method.
   * @param {String} method Method to set.
   * @return {Request} This request.
   * @example request.setMethod('POST');
   */
  setMethod(method: HttpMethod): HttpMethod {
    this.method = method.toUpperCase() as HttpMethod;
    return this.method;
  }

  /**
   * @desc Define a specific body param value.
   * @param {String} paramName Param to set.
   * @param {String} paramValue Value to set.
   * @return {Request} This request.
   * @example request.setBodyParam('foo', 'bar');
   */
  setBodyParam(paramName: string, paramValue: string | number): this {
    (this.body as Record<string, any>)[paramName] = paramValue;
    return this;
  }

  /**
   * @desc Define multiple body params values.
   * @param {Object} body An object containing multiple values.
   * @return {Request} This request.
   * @example request.setBodyParams({ 'foo': 'bar', 'bar': 'foo' });
   */
  setBodyParams(body: ParamCollection | RequestBody): this {
    Object.assign(this.body, body);
    return this;
  }

  /**
   * @desc Directly define body request.
   * @param {FormData | Array<unknown>} body Raw value of the body.
   * @return {Request} This request.
   * @example
   * const formData = new FormData();
   * request.setRawBody(formData);
   */
  setRawBody(body: FormData | Array<unknown>): this {
    this.body = body;
    return this;
  }

  /**
   * @desc Get serialized body params.
   * @return {String} The serialized body.
   * @example request.getSerializedBody();
   */
  getSerializedBody(): string | FormData {
    // @ts-ignore
    return this.body instanceof globals.window.FormData
      ? (this.body as FormData)
      : JSON.stringify(this.body);
  }

  /**
   * @desc Define a specific query param value.
   * @param {String} paramName Param to set.
   * @param {String} paramValue Value to set.
   * @return {Request} This request.
   * @example request.setQueryParam('foo', 'bar');
   */
  setQueryParam(paramName: string, paramValue: string | number): this {
    this.queryParams[paramName] = paramValue;
    return this;
  }

  /**
   * @desc Define multiple query params.
   * @param {Object} queryParams An object containing multiple values.
   * @return {Request} This request.
   * @example request.setQueryParams({ 'foo': 'bar', 'bar': 'foo' });
   */
  setQueryParams(queryParams: ParamCollection): this {
    Object.assign(this.queryParams, queryParams);
    return this;
  }

  /**
   * @desc Define a specific upload option value.
   * @param {String} optionName Option to set.
   * @param {String} optionValue Value to set.
   * @return {Request} This request.
   * @example
   * request.setUploadOption('progress', (...data) => {
   *     console.log(data);
   * });
   */
  setUploadOption(optionName: string, optionValue: string | number | Function): this {
    this.uploadOptions[optionName] = optionValue;
    return this;
  }

  /**
   * @desc Define multiple upload options.
   * @param {Object} uploadOptions An object containing multiple values.
   * @return {Request} This request.
   * @example request.setUploadOptions({
   *     progress() {},
   *     error() {},
   * });
   */
  setUploadOptions(uploadOptions: ParamCollection): this {
    Object.assign(this.uploadOptions, uploadOptions);
    return this;
  }

  /**
   * @desc Get serialized query params.
   * @return {String} The serialized body.
   * @example request.getSerializedQueryParams();
   */
  getSerializedQueryParams(): string {
    return serialized(this.queryParams);
  }

  /**
   * @desc Define a specific header value.
   * @param {String} headerName Header to set.
   * @param {String} headerValue Value to set.
   * @return {Request} This request.
   * @example request.setHeader('Content-Type', 'text/html');
   */
  setHeader<H extends keyof RequestHeaders>(headerName: H, headerValue?: RequestHeaders[H]): this {
    if (headerValue === undefined) {
      delete this.headers[headerName];
    } else {
      this.headers[headerName] = headerValue;
    }

    return this;
  }

  /**
   * @desc Define multiple headers values.
   * @param {RequestHeaders} headers An object containing multiple headers values.
   * @return {Request} This request.
   * @example request.setHeaders({ 'Content-Type': 'text/html', Accept: 'application/json' });
   */
  setHeaders(headers: RequestHeaders): this {
    Object.assign(this.headers, headers);
    return this;
  }

  /**
   * @desc Define a specific route param value.
   * @param {String} paramName Param to set.
   * @param {String} paramValue Value to set.
   * @return {Request} This request.
   * @example request.setRouteParam('foo', 'bar');
   */
  setRouteParam(paramName: string, paramValue: string | number): this {
    this.routeParams[paramName] = paramValue;
    return this;
  }

  /**
   * @desc Define multiple route params.
   * @param {Object} routeParams An object containing multiple values.
   * @return {Request} This request.
   * @example request.setRouteParams({ 'foo': 'bar', 'bar': 'foo' });
   */
  setRouteParams(routeParams: ParamCollection): this {
    Object.assign(this.routeParams, routeParams);
    return this;
  }

  /**
   * @desc Define the `Range` header for pagination.
   * @param {Number} start Pagination start.
   * @param {Number} end Pagination end.
   * @return {Request} This request.
   * @example request.paginate(5, 10);
   */
  paginate(start: number = 0, end?: number): this {
    if (!end) {
      return this;
    }

    const rangeStart = start || 0;

    const itemsPerPage = end - rangeStart + 1;
    const pageToFetch = Math.floor(rangeStart / itemsPerPage) + 1;

    return this.setHeader('Range', `items=${rangeStart}-${end}`).setHeader(
      'Pagination',
      `page=${pageToFetch},items=${itemsPerPage}`
    );
  }

  /**
   * @desc Add an interceptor to run before executing the request.
   * @param {Function} interceptor The function to execute.
   * It takes a deep copy of request data must return these altered data.
   * The interceptor can also return a Promise which must resolve altered data.
   * @return {Request} This request.
   * @example
   * request.addRequestInterceptor((requestData) => {
   *  const finalData = { ...requestData };
   *  finalData.requestOptions.headers.myCustomHeader = 'customHeaderValue';
   *  return finalData;
   * });
   */
  addRequestInterceptor(interceptor: Interceptor<InterceptorRequestData>): this {
    this.requestInterceptors.push(interceptor);
    return this;
  }

  /**
   * @desc Add an interceptor to run after executing the request.
   * @param {Function} interceptor The function to execute.
   * It takes a deep copy of request response to resolve and must return this altered response.
   * The interceptor can also return a Promise which must resolve altered response.
   * Note that the interceptor will be call only if the request success.
   * @return {Request} This request.
   * @example
   * request.addResponseInterceptor((requestResponse) => {
   *  const finalResponse = { ...requestResponse };
   *  finalResponse.responseHeaders.myCustomHeader = 'customHeaderValue';
   *  return finalResponse;
   * });
   */
  addResponseInterceptor(interceptor: Interceptor<Response>): this {
    this.responseInterceptors.push(interceptor);
    return this;
  }

  /**
   * @desc Return request URL with correct route and query params.
   * @return {String} The request URL.
   * @example const url = this.getUrl();
   */
  getUrl(): string {
    let url = this.url.replace(/\/:([a-zA-Z0-9_]+)/gi, ($0, $1) =>
      this.routeParams[$1] ? `/${this.routeParams[$1]}` : ''
    );

    const queryParams = this.getSerializedQueryParams();

    if (queryParams) {
      url += `?${queryParams}`;
    }

    return url;
  }

  /**
   * @desc Abort current Ajax request.
   * @return {Boolean} `true` if request has been aborted, `false` otherwise.
   * @example request.abort();
   */
  abort(): boolean {
    if (!this.currentXhr) {
      return false;
    }

    this.currentXhr.abort();
    this.currentXhr = null;

    return true;
  }

  /**
   * @desc Perform an Ajax request.
   * @return {Promise} The promise handling the request.
   * @example request.execute().then(() => {}).catch(() => {});
   */
  execute(): Promise<ApiResponse<EntityType>> {
    return new Promise((resolve, reject) => {
      const requestOptions: RequestOptions = {
        url: this.getUrl(),
        method: this.method,
        headers: this.headers,
        body: this.getSerializedBody(),
      };

      const finalRequestDataPromise = this.applyRequestInterceptors({
        requestOptions,
        requestResponse: null,
      });

      finalRequestDataPromise
        .then((finalRequestData) => {
          if (finalRequestData.requestResponse) {
            this.resolve(finalRequestData.requestResponse, resolve, reject);
            return;
          }

          const stringifiedOptions = JSON.stringify(finalRequestData.requestOptions);
          const queuedRequest: QueuedRequest<EntityType> = { request: this, resolve, reject };

          if (Request.queuedRequests[stringifiedOptions]) {
            this.currentXhr = Request.queuedRequests[stringifiedOptions][0].request.currentXhr;
            Request.queuedRequests[stringifiedOptions].push(queuedRequest);
            return;
          }

          Request.queuedRequests[stringifiedOptions] = [queuedRequest];
          this.currentXhr = ajax(
            finalRequestData.requestOptions,
            (statusCode: number, response: string, xhr: XMLHttpRequest) => {
              const responseHeaders = getParsedResponseHeaders(xhr.getAllResponseHeaders());

              let body;

              try {
                body = JSON.parse(response || '{}');
              } catch (e) {
                body = response;
              }

              const finalResponse = { body, statusCode, responseHeaders };

              Request.resolveAllRequests(stringifiedOptions, finalResponse);
              this.currentXhr = null;
            },
            // @ts-ignore
            this.uploadOptions
          );
        })
        .catch((error) => this.resolve(error, resolve, reject));
    });
  }

  /**
   * @private
   * @desc Resolve this request with given response.
   * @param {Object} response Response to resolve.
   * @param {Function} resolve Function to execute if response is success.
   * @param {Function} reject Function to execute if response is failure.
   * @example request.resolve(myResponse, resolve, reject);
   */
  protected resolve(response: Response, resolve: PromiseResolver, reject: PromiseResolver) {
    if (isStatusOk(response.statusCode)) {
      const finalResponse = { ...response };

      if (isPartialResponse(response.statusCode)) {
        finalResponse.pagination = getPaginationFromHeaders(response.responseHeaders, this);
      }

      if (response.responseHeaders.link) {
        finalResponse.links = linkHeaderParser.parse(response.responseHeaders.link.trim());
      }

      this.applyResponseInterceptors(finalResponse).then(resolve);
    } else {
      reject(response);
    }
  }

  /**
   * @private
   * @desc
   * Alter the given options by applying all the request interceptors of this instance.
   * @param {RequestData} requestData Request data to alter.
   * @return {Promise} A promise resolving the altered request data.
   * @example request.applyRequestInterceptors(requestData).then((alteredData) => {});
   */
  protected applyRequestInterceptors(
    requestData: InterceptorRequestData
  ): Promise<InterceptorRequestData> {
    return this.requestInterceptors.reduce(
      (promiseRequestData, interceptor) =>
        promiseRequestData.then(
          (responsePromise) => interceptor(responsePromise) || responsePromise
        ),
      Promise.resolve(requestData)
    );
  }

  /**
   * @private
   * @desc
   * Alter the given request response by applying all the response interceptors of this instance.
   * @param {RequestResponse} response The response to alter.
   * @return {Promise} A promise resolving the altered response.
   * @example request.applyResponseInterceptors(requestResponse).then((alteredResponse) => {});
   */
  protected applyResponseInterceptors(response: Response) {
    return this.responseInterceptors.reduce(
      (promiseResponse, interceptor) =>
        promiseResponse.then(
          (responsePromise) => interceptor(deepClone(responsePromise)) || responsePromise
        ),
      Promise.resolve(response)
    );
  }

  static queuedRequests: Record<string, QueuedRequest<ResponseBody | void>[]> = {};

  static resolveAllRequests(stringifiedOptions: string, response: Response): void {
    const allQueuedRequests = Request.queuedRequests[stringifiedOptions] || [];

    allQueuedRequests.forEach((queuedRequest) => {
      const { request, resolve, reject } = queuedRequest;
      request.resolve(response, resolve, reject);
    });

    delete Request.queuedRequests[stringifiedOptions];
  }
}

export default Request;
